import {
  Glass,
  ScreenLabel,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";
import { JourneyProgress, OnboardingFooter, DataNeedIcon } from "./journey-shared";

function DataSourceRow({ type, label, status, action }) {
  const isReady = status === "ready";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 0",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      {isReady ? (
        <svg width="17" height="17" viewBox="0 0 17 17" style={{ flexShrink: 0 }}>
          <circle cx="8.5" cy="8.5" r="8.5" fill="var(--color-kin-green-soft)" />
          <path
            d="M5 8.5 L7.3 10.8 L12 6.2"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <div
          style={{
            width: 17,
            height: 17,
            borderRadius: "50%",
            background: alpha("var(--color-kin-orange)", "12"),
            border: "2px solid var(--color-kin-orange)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--color-kin-orange)",
            }}
          />
        </div>
      )}

      <DataNeedIcon
        type={type}
        accent={isReady ? "var(--color-kin-green-soft)" : "var(--color-kin-orange)"}
      />

      <div style={{ flex: 1 }}>
        <BodyText size={10} weight={600}>
          {label}
        </BodyText>
        <BodyText
          size={8}
          color={isReady ? "var(--color-kin-green-soft)" : "var(--color-kin-orange)"}
          weight={500}
        >
          {isReady ? "In your vault" : "Missing"}
        </BodyText>
      </div>

      {!isReady && action && (
        <div
          style={{
            padding: "4px 10px",
            borderRadius: 20,
            background: alpha("var(--color-kin-orange)", "14"),
            border: borderAlpha("var(--color-kin-orange)", "30"),
          }}
        >
          <BodyText size={8} color="var(--color-kin-orange)" weight={700}>
            {action}
          </BodyText>
        </div>
      )}
    </div>
  );
}

export function JourneyVaultSetupScreen() {
  const readyCount = 2;
  const totalCount = 4;

  const items = [
    { type: "wearable", label: "Apple Health", status: "ready" },
    { type: "sleep", label: "Sleep data", status: "ready" },
    { type: "wearable", label: "HRV / heart rate", status: "missing", action: "Connect" },
    { type: "lab", label: "Blood panel", status: "missing", action: "Upload" },
  ];

  return (
    <div
      style={{
        padding: "8px 16px 0",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%",
        position: "relative",
      }}
    >
      <JourneyProgress current={2} />

      {/* Header — contextual to chosen skill */}
      <div style={{ marginBottom: 2 }}>
        <ScreenLabel>Step 2 of 4</ScreenLabel>
        <DisplayText size={20}>Sleep Deep Dive</DisplayText>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ marginTop: 3, lineHeight: 1.45 }}
        >
          Check which data you already have and what's missing for the best results.
        </BodyText>
      </div>

      {/* Progress bar */}
      <Glass style={{ padding: "10px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <BodyText size={10} weight={700} color="var(--color-kin-black)">
            {readyCount} of {totalCount} data sources ready
          </BodyText>
          <BodyText size={9} color="var(--color-kin-green-soft)" weight={700}>
            {Math.round((readyCount / totalCount) * 100)}%
          </BodyText>
        </div>
        <div
          style={{
            height: 5,
            borderRadius: 3,
            background: "rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(readyCount / totalCount) * 100}%`,
              height: "100%",
              borderRadius: 3,
              background:
                "linear-gradient(90deg, var(--color-kin-green-soft), var(--color-kin-green-mid))",
            }}
          />
        </div>
      </Glass>

      {/* Data source checklist */}
      <div>
        <ScreenLabel style={{ marginBottom: 6, display: "block" }}>Data sources</ScreenLabel>
        <Glass style={{ padding: "0 12px" }}>
          {items.map((item) => (
            <DataSourceRow key={`${item.type}-${item.label}`} {...item} />
          ))}
        </Glass>
      </div>

      <BodyText size={8.5} color="var(--color-kin-muted)" weight={500} style={{ lineHeight: 1.45 }}>
        You can add more sources later. First insight unlocks as soon as minimum data is connected.
      </BodyText>

      <OnboardingFooter label="Continue" enabled={true} showSkip={true} />
    </div>
  );
}
